import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from '../assets/logo.png';
import { useBuilding } from "../context/buildingContext";
import { fetchApi } from "../utils/fetchApi";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from '../context/userContext';
import { displayFloorAndDoorByName, sortByApartmentName } from '../utils/helpers';
import { registerPush, getCurrentSubscription, isNotifEnabledFrom, } from '../utils/pushNotification';
import DropdownSelector from '../components/DropdownSelector';
import { cacheBuildingImage, getCachedBuildingImage } from '../utils/imageHelpers';

import iconCross from '../assets/icon-cross.svg';
import iconCheck from '../assets/icon-check.svg';

const VAPID_PUBLIC_KEY = "BMYhLrCePlacxZOJ3SF9daM3qeX9W6zyJFAWY-_jVtmMl81C_K9lIAjg7fSWN8jwEloYIpu2DM_5ouI1JwV-d-c";

export interface Building {
  id: number;
  country: string;
  postalCode: number;
  city: string;
  street: string;
  humidity?: number;
  temperature?: number,
  name?: string;
  disabled?: boolean;
  childLock?: boolean;
  childLockTemperatureDegC?: number
}

interface Apartment {
  apartment_id: number;
  name: string;
  building: number;
  online: boolean
}

const BuildingSelector: React.FC = () => {
  const { buildingId, setBuildingData, clearBuildingData } = useBuilding();
  const { logout } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(buildingId);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [currentApartment, setCurrentApartment] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isNotificationsEnabled, setIsNotificationsEnabled] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  let buildingsVariable: Building[] = [];

  useEffect(() => {
    const appContainer = document.querySelector('.app-container') as HTMLElement;
    if (!appContainer) return;

    appContainer.classList.add('buildinSelector');
    const numericBuildingId = currentBuildingId ? parseInt(currentBuildingId, 10) : null;

    if (!numericBuildingId) {
      appContainer.style.setProperty('--dynamic-background', 'url(/buildings/default.webp)');
      return;
    }

    // Azonnal mutatjuk a cache-elt képet, ha van
    const cached = getCachedBuildingImage(numericBuildingId);
    if (cached) {
      appContainer.style.setProperty('--dynamic-background', `url(${cached})`);
    }

    // API-ból frissítjük és mentjük
    fetchApi('get_building_image', { building_id: numericBuildingId })
      .then((result: { image_base64: string | null }) => {
        if (result.image_base64) {
          const dataUrl = cacheBuildingImage(numericBuildingId, result.image_base64);
          appContainer.style.setProperty('--dynamic-background', `url(${dataUrl})`);
        } else if (!cached) {
          appContainer.style.removeProperty('--dynamic-background');
        }
      })
      .catch(() => {
        if (!cached) {
          appContainer.style.removeProperty('--dynamic-background');
        }
      });
  }, [currentBuildingId]);

  // induláskor: olvassuk ki a tényleges subscription-t
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const sub = await getCurrentSubscription();
      if (!cancelled) {
        setIsNotificationsEnabled(isNotifEnabledFrom(sub));
        setNotifLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const buildingOptions = {
    '': t("buildingSelector.selectBuilding"),
    ...buildings.reduce((acc, building) => {
      acc[building.id.toString()] = `${building.country} ${building.postalCode} ${building.city} ${building.street}`;
      return acc;
    }, {} as Record<string, string>)
  };

  const apartmentOptions = {
    '': t("buildingSelector.selectApartment"),
    ...apartments.reduce((acc, apartment) => {
      acc[apartment.apartment_id.toString()] = String(displayFloorAndDoorByName(apartment.name, true));
      return acc;
    }, {} as Record<string, string>)
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const buildingParam = searchParams.get("building");
    const apartmentParam = searchParams.get("apartment");

    if (buildingParam && apartmentParam) {
      setCurrentBuildingId(buildingParam);
      setCurrentApartment(apartmentParam);
    }
  }, [location.search]);

  // Épületek betöltése
  useEffect(() => {
    const loadBuildings = async () => {
      try {
        const response: [number, string, number, string, string, number, number][] =
          await fetchApi("get_building_access_rights");

        const formattedBuildings: Building[] = response.map(([id, country, postalCode, city, street]) => ({
          id,
          country,
          postalCode,
          city,
          street,
        })).sort((a, b) =>
          `${a.city} ${a.street}`.localeCompare(`${b.city} ${b.street}`, 'hu')
        );

        buildingsVariable = formattedBuildings
        setBuildings(formattedBuildings);

        // Ha csak 1 épület van és nincs paraméterből jövő érték
        if (formattedBuildings.length === 1 && !currentBuildingId) {
          setCurrentBuildingId(formattedBuildings[0].id.toString());
        }
      } catch (err) {
        console.error("Error loading buildings:", err);
        setError(t("buildingSelector.error.buildingLoad"));
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();
  }, [t]);

  // Apartmanok betöltése
  useEffect(() => {
    const loadApartments = async () => {
      if (currentBuildingId) {
        try {
          const response: [number, number, string, boolean][] =
            await fetchApi("get_apartment_access_rights", { building_id: currentBuildingId });

          const formattedApartments: Apartment[] = response.map(([building_id, apartment_id, name, online]) => ({
            apartment_id: apartment_id,
            building: building_id,
            name,
            online
          }));

          setApartments(sortByApartmentName(formattedApartments));

          // Ha paraméterből kaptuk az apartmant → azonnali mentés
          const searchParams = new URLSearchParams(location.search);
          const buildingParam = searchParams.get("building");
          const apartmentParam = searchParams.get("apartment");

          if (buildingParam && apartmentParam) {
            const match = formattedApartments.find(a => a.apartment_id.toString() === apartmentParam);
            if (match) {
              redirectToDashboardWithContext(buildingParam, apartmentParam, match.name);
            }
          }

          // Ha nincs paraméter és csak 1 apartman van → auto mentés
          if (!searchParams.get("apartment") && formattedApartments.length === 1 && buildingsVariable.length === 0) {
            const currentApartmentId = formattedApartments[0].apartment_id.toString();
            setCurrentApartment(currentApartmentId);
            redirectToDashboardWithContext(currentBuildingId, currentApartmentId, formattedApartments[0].name);
          }
        } catch (err) {
          console.error("Error loading apartments:", err);
          setError(t("buildingSelector.error.apartmentLoad"));
        }
      }
    };

    loadApartments();
  }, [currentBuildingId, t, location.search]);

  const handleBuildingChange = (value: string) => {
    setCurrentBuildingId(value);
    setCurrentApartment(null);
  };

  const handleApartmentChange = (value: string) => {
    setCurrentApartment(value);
  };

  const getSelectedApartmentData = (apartmentId: string) =>
    apartments.find((apt) => apt.apartment_id.toString() === apartmentId);

  const redirectToDashboardWithContext = (buildingId: string, apartmentId: string, name?: string) => {
    if (buildingId && apartmentId) {
      const selectedApartment = getSelectedApartmentData(apartmentId);
      setBuildingData(buildingId, apartmentId, name ?? selectedApartment?.name ?? "");
      setError(null);
      navigate("/dashboard");
    }
  };

  const handleSave = () => {
    if (currentBuildingId && currentApartment) {
      const appContainer = document.querySelector('.app-container') as HTMLElement;
      if (appContainer) {
        appContainer.classList.remove('buildinSelector');
      }
      redirectToDashboardWithContext(currentBuildingId, currentApartment);
    } else {
      setError(t("buildingSelector.error.selectBoth"));
    }
  };

  const handleLogout = () => {
    clearBuildingData();
    logout();
  };

  const handleNotificationsToggle = async () => {
    // ha töltünk, ne engedjük kattintani
    if (notifLoading) return;

    const next = !isNotificationsEnabled;

    if (next) {
      // BEKAPCS: regisztrálunk (ez már hívja a backend 'notification_subscribe'-ot)
      try {
        setNotifLoading(true);
        await registerPush(VAPID_PUBLIC_KEY);
        // ha a regisztráció sikerült, a SW-ben lesz subscription
        const sub = await getCurrentSubscription();
        setIsNotificationsEnabled(isNotifEnabledFrom(sub));
      } catch (e: any) {
        alert('Hiba az értesítések bekapcsolásakor: ' + e.message);
        setIsNotificationsEnabled(false);
      } finally {
        setNotifLoading(false);
      }
    } else {
      // KIKAPCS: leiratkozunk és szólunk a backendnek is
      try {
        setNotifLoading(true);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();

        const endpoint = sub?.endpoint;
        if (sub) await sub.unsubscribe();

        // Ha a backend oldalán is tárolod, jelezd a leiratkozást (példanév)
        if (endpoint) {
          await fetchApi('notification_subscribe', {
            subscription_obj: JSON.stringify({}),
          });
        }

        setIsNotificationsEnabled(false);
      } catch (e: any) {
        alert('Hiba az értesítések kikapcsolásakor: ' + e.message);
        // ha nem sikerült leiratkozni, próbáljuk szinkronban tartani az UI-t a ténnyel:
        const stillSub = await getCurrentSubscription();
        setIsNotificationsEnabled(isNotifEnabledFrom(stillSub));
      } finally {
        setNotifLoading(false);
      }
    }
  };


  return (
    <div>
        <div className='buildingSelectorLogo'>
          <img className="logo" src={logo} alt="Logo" />
        </div>
      <div className="buildingSelectorContent">
        <div className="buildingSelectorContainer">
          {loading ? (
            <p>{t("loading")}</p>
          ) : (
            <>
              <h3 className="selectorTitle">{t("buildingSelector.selectApartmentTitle")}</h3>
              <div className="selector">
                <label htmlFor="building">{t("buildingSelector.building")}</label>
                <DropdownSelector
                  label={t("buildingSelector.building")}
                  options={buildingOptions}
                  value={currentBuildingId || ""}
                  onChange={handleBuildingChange}
                />
                <>
                  <label htmlFor="apartment">{t("buildingSelector.apartment")}</label>
                  <DropdownSelector
                    label={t("buildingSelector.apartment")}
                    options={apartmentOptions}
                    value={currentApartment || ""}
                    onChange={handleApartmentChange}
                    disabled={!currentBuildingId}
                  />
                </>
              </div>

              {error && <div className="error">{error}</div>}
              <button className="button" onClick={handleSave}>
                {t("buildingSelector.save")}
              </button>

              <div className="notificationCard">
                <div className="cardText">
                  <label htmlFor="notifications">{t('enableNotifications')}</label>
                </div>
                <div className="toggleSwitch">
                  <input
                    type="checkbox"
                    id="notifications"
                    checked={isNotificationsEnabled}
                    onChange={handleNotificationsToggle}
                  />
                  <label htmlFor="notifications" className="toggleLabel">
                    <span className="toggleSlider">
                      <img src={iconCross} alt="off" className="icon icon-cross" />
                      <img src={iconCheck} alt="on" className="icon icon-check" />
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
        <p className='link' onClick={handleLogout}>
          {t('logOut')}
          <div className="cardIcon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </p>
      </div>
     
    </div>
  );
};


export default BuildingSelector;
